export function lazyDeepCopy(obj: any) {
  return JSON.parse(JSON.stringify(obj));
}

export function fetchApi(uri: string, args: RequestInit) {
  const isDesktop = !!window.chrome?.webview;
  const argsWithHeaders = {
    ...args,
    headers: {
      ...getHeaders(),
      ...args.headers,
    },
  };

  const host = uri.startsWith('/api/v3') ? import.meta.env.VITE_API_V3_DOMAIN : import.meta.env.VITE_DOMAIN;

  return fetch(`${host}${uri}`, argsWithHeaders).then((request) => {
    const url = window.location.href;
    /*
      If we get an unauthorized request on the desktop application,
      the page should reload with a new token- if we are in a web environment redirect to /login
     */
    if (!url.includes('/login') && request.status === 401) {
      if (isDesktop) return request;

      window.location.href = '/login'; // No need to return here (navigation), TS will throw a fit
    }
    return request;
  });
}

export function fetchApiWithAbort(uri: string, args?: RequestInit): [Promise<Response>, AbortController] {
  const controller = new AbortController();
  return [fetchApi(uri, { ...args, signal: controller.signal }), controller];
}

export async function fetchApiWithRetry(uri: string, args: RequestInit, maxRetries = 3, baseDelay = 1000): Promise<Response> {
  let lastError: Error;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetchApi(uri, args);

      if (response.ok) {
        return response;
      }

      if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        return response;
      }

      if (response.status >= 500 || response.status === 429) {
        lastError = new Error(`HTTP ${response.status}: ${response.statusText}`);

        if (attempt === maxRetries) {
          return response;
        }

        const delay = baseDelay * 2 ** attempt + Math.random() * 1000;
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt === maxRetries) {
        throw lastError;
      }

      const delay = baseDelay * 2 ** attempt + Math.random() * 1000;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  lastError ||= new Error('Server never responded');
  throw lastError;
}

export function getHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
}

export function getAuthHeaders(): HeadersInit {
  return {
    'ZUMO-API-VERSION': '2.0.0',
    'X-ZUMO-AUTH': `${window.sessionStorage.getItem('zumo') || window.localStorage.getItem('zumo')}`,
  };
}
